import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, ImagePlus, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { generateSalonBio, submitSalonOnboarding, uploadSalonOnboardingPhoto } from "../lib/api";

const SUBURBS = ["CBD", "Docklands", "Southbank", "Fitzroy", "Fitzroy North", "Collingwood", "Carlton", "Carlton North", "Brunswick", "Brunswick East", "Northcote", "Thornbury", "Preston", "Richmond", "Cremorne", "South Yarra", "Prahran", "Windsor", "Armadale", "Toorak", "Hawthorn", "Hawthorn East", "Kew", "Balaclava", "St Kilda", "Elwood", "Port Melbourne", "Albert Park", "Middle Park", "Other"];

const steps = [
  { n: 1, label: "Studio" },
  { n: 2, label: "About" },
  { n: 3, label: "First opening" },
];

export default function SalonOnboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [bioWriting, setBioWriting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoUploads, setPhotoUploads] = useState([]);
  const [photoDraftId] = useState(() => (window.crypto?.randomUUID?.() || `studio-${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, ""));
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [form, setForm] = useState({
    salon_name: "",
    contact_email: "",
    suburb: "",
    categories: [],
    stylist_count: 1,
    tagline: "",
    about: "",
    hours: "",
    google_reviews_enabled: false,
    google_reviews_url: "",
    current_booking_system: "",
    current_booking_system_other: "",
    first_service: "",
    first_original_price: "",
    first_offer_price: "",
    first_time: "",
    first_duration_min: "",
    referral_code: "",
  });

  useEffect(() => { const code = new URLSearchParams(location.search).get("ref"); if (code) { window.sessionStorage.setItem("fyllr_referral_code", code.toUpperCase()); set("referral_code", code.toUpperCase()); } else { set("referral_code", window.sessionStorage.getItem("fyllr_referral_code") || ""); } }, [location.search]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canNext = () => {
    if (step === 1) return form.salon_name.trim() && form.contact_email.trim() && form.suburb && termsAccepted;
    if (step === 2) return form.stylist_count > 0 && (!form.google_reviews_enabled || form.google_reviews_url.trim());
    if (step === 3) return true;
    return true;
  };

  const submit = async () => {
    try {
      setLoading(true);
      const payload = {
        ...form,
        stylist_count: Number(form.stylist_count) || 1,
        first_original_price: form.first_original_price ? Number(form.first_original_price) : undefined,
        first_offer_price: form.first_offer_price ? Number(form.first_offer_price) : undefined,
        first_duration_min: form.first_duration_min ? Number(form.first_duration_min) : undefined,
        google_reviews_url: form.google_reviews_enabled ? form.google_reviews_url.trim() : undefined,
        onboarding_photo_draft_id: photoDraftId,
        onboarding_photo_ids: photoUploads.map((photo) => photo.id),
        terms_accepted: termsAccepted,
      };
      const record = await submitSalonOnboarding(payload);
      navigate(`/salon/admin?onboarding=complete${record?.billing_ready ? "" : "&billing=unavailable"}`, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit — check your details and try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const writeBio = async () => {
    if (!form.salon_name.trim() || !form.suburb) {
      toast.error("Add your studio name and suburb before writing your bio.");
      return;
    }
    setBioWriting(true);
    set("about", "");
    try {
      await generateSalonBio({ salon_name: form.salon_name, suburb: form.suburb, categories: form.categories, tagline: form.tagline || undefined }, (text) => set("about", text));
    } catch (error) {
      toast.error(error?.message || "Could not write your bio. Please try again.");
    } finally {
      setBioWriting(false);
    }
  };

  const addPhotos = async (files) => {
    const selected = Array.from(files || []).slice(0, 5 - photoUploads.length);
    if (!selected.length) return;
    setUploading(true);
    try {
      const additions = [];
      for (const file of selected) additions.push(await uploadSalonOnboardingPhoto(photoDraftId, file));
      setPhotoUploads((current) => [...current, ...additions]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not upload that photo.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="pt-16 min-h-screen" data-testid="salon-onboard-page">
      <div className="max-w-[880px] mx-auto px-6 md:px-10 py-16">
        {/* Progress */}
        <div className="flex items-center justify-between mb-10">
          <Link to="/salon" className="text-xs text-ink/50 hover:text-ink transition-colors inline-flex items-center gap-2" data-testid="onboard-back">
            <ArrowLeft size={14} strokeWidth={1.5} /> Back to salon
          </Link>
          <p className="text-xs tracking-[0.22em] uppercase text-mocha">
            {`Step ${Math.min(step, 3)} of 3`}
          </p>
        </div>

        <div className="flex items-center gap-2 mb-12">
          {steps.map((s) => (
            <div key={s.n} className="flex-1 flex items-center gap-2">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] ${
                  step > s.n ? "bg-espresso text-bone" : step === s.n ? "bg-espresso text-bone" : "bg-sand text-mocha"
                }`}
              >
                {step > s.n ? <Check size={12} strokeWidth={2} /> : s.n}
              </span>
              <span className={`text-xs ${step >= s.n ? "text-ink" : "text-ink/40"}`}>{s.label}</span>
              {s.n < 3 && <span className="flex-1 h-px bg-sand ml-2" />}
            </div>
          ))}
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-8" data-testid="onboard-step-1">
            <div>
              <h1 className="font-serif text-4xl md:text-5xl leading-tight">Tell us about your business.</h1>
              <p className="mt-2 text-sm text-ink/60">Just the basics — salon, spa, clinic, or wellness studio.</p>
            </div>

            <label className="block">
              <span className="block text-xs text-ink/60 mb-1.5">Business name</span>
              <input
                type="text"
                value={form.salon_name}
                onChange={(e) => set("salon_name", e.target.value)}
                className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                placeholder="Studio Neue / The Bathhouse"
                data-testid="onboard-salon-name"
              />
            </label>

            <label className="block">
              <span className="block text-xs text-ink/60 mb-1.5">Contact email</span>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => set("contact_email", e.target.value)}
                className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                placeholder="owner@studio.com"
                data-testid="onboard-email"
              />
            </label>

            <label className="block">
              <span className="block text-xs text-ink/60 mb-1.5">Suburb</span>
              <select value={form.suburb} onChange={(e) => set("suburb", e.target.value)} className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px] bg-bone" data-testid="onboard-suburb">
                <option value="">Choose suburb…</option>
                {SUBURBS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="flex items-start gap-3 text-sm leading-relaxed text-ink/65" data-testid="onboard-terms-row"><input type="checkbox" checked={termsAccepted} onChange={(event) => setTermsAccepted(event.target.checked)} className="mt-0.5 h-4 w-4 accent-espresso" data-testid="onboard-terms-checkbox" /><span>I agree to the FYLLR <Link to="/terms" className="text-espresso underline underline-offset-2" data-testid="onboard-terms-link">Terms &amp; Conditions</Link>, <Link to="/privacy" className="text-espresso underline underline-offset-2" data-testid="onboard-privacy-link">Privacy Policy</Link>, and <Link to="/partner-agreement" target="_blank" rel="noopener noreferrer" className="text-espresso underline underline-offset-2" data-testid="onboard-partner-agreement-link">Partner Agreement</Link>.</span></label>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-8" data-testid="onboard-step-2">
            <div>
              <h1 className="font-serif text-4xl md:text-5xl leading-tight">A little more about you.</h1>
              <p className="mt-2 text-sm text-ink/60">Clients will see this on your profile. All optional except team size.</p>
            </div>

            <label className="block">
              <span className="block text-xs text-ink/60 mb-1.5">Tagline (optional)</span>
              <input
                type="text"
                value={form.tagline}
                onChange={(e) => set("tagline", e.target.value)}
                className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                placeholder="A quiet colour house, hidden down Keele St."
                data-testid="onboard-tagline"
              />
            </label>

            <div>
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-3"><span className="block text-xs text-ink/60">About (optional)</span><button type="button" onClick={writeBio} disabled={bioWriting} className="inline-flex items-center gap-1.5 text-xs text-espresso hover:text-ink disabled:opacity-50" data-testid="onboard-write-bio">{bioWriting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}{form.about ? "Regenerate with AI" : "Write with AI"}</button></div>
              <textarea
                value={form.about}
                onChange={(e) => set("about", e.target.value)}
                rows={4}
                className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px] resize-none"
                placeholder="Tell clients what makes your space feel like home…"
                data-testid="onboard-about"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <label className="block">
                <span className="block text-xs text-ink/60 mb-1.5">Opening hours (optional)</span>
                <input
                  type="text"
                  value={form.hours}
                  onChange={(e) => set("hours", e.target.value)}
                  className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                  placeholder="Tue–Sat · 9am–7pm"
                  data-testid="onboard-hours"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-ink/60 mb-1.5">Team size</span>
                <input
                  type="number"
                  min="1"
                  value={form.stylist_count}
                  onChange={(e) => set("stylist_count", e.target.value)}
                  className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                  data-testid="onboard-stylists"
                />
              </label>
            </div>
            <label className="block"><span className="block text-xs text-ink/60 mb-1.5">What booking system do you currently use?</span><select value={form.current_booking_system} onChange={(e) => { set("current_booking_system", e.target.value); if (e.target.value !== "Other") set("current_booking_system_other", ""); }} className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px] bg-bone" data-testid="onboard-booking-system"><option value="">Select a system…</option>{["Timely", "Shortcuts", "Phorest", "Square Appointments", "Kitomba", "Fresha", "Other"].map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
            {form.current_booking_system === "Other" && <label className="block"><span className="block text-xs text-ink/60 mb-1.5">Which system?</span><input value={form.current_booking_system_other} onChange={(e) => set("current_booking_system_other", e.target.value)} className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]" data-testid="onboard-booking-system-other" /></label>}
            <div className="border-y border-sand/70 py-6" data-testid="onboard-google-reviews-section"><div className="flex flex-wrap items-center justify-between gap-4"><div><p className="text-sm text-ink" data-testid="onboard-google-reviews-label">Do you have Google Reviews?</p><p className="mt-1 text-xs text-ink/55">Add your profile so we can connect it to your FYLLR venue.</p></div><div className="inline-flex border border-sand rounded-full p-1" data-testid="onboard-google-reviews-toggle"><button type="button" onClick={() => set("google_reviews_enabled", true)} className={`rounded-full px-4 py-1.5 text-xs transition-colors ${form.google_reviews_enabled ? "bg-espresso text-bone" : "text-ink/55"}`} data-testid="onboard-google-reviews-yes">Yes</button><button type="button" onClick={() => { set("google_reviews_enabled", false); set("google_reviews_url", ""); }} className={`rounded-full px-4 py-1.5 text-xs transition-colors ${!form.google_reviews_enabled ? "bg-espresso text-bone" : "text-ink/55"}`} data-testid="onboard-google-reviews-no">No</button></div></div>{form.google_reviews_enabled && <label className="mt-5 block"><span className="block text-xs text-ink/60 mb-1.5">Google Reviews URL</span><input type="url" required value={form.google_reviews_url} onChange={(e) => set("google_reviews_url", e.target.value)} className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]" placeholder="https://g.page/r/your-studio/review" data-testid="onboard-google-reviews-url" /></label>}</div>
            <div data-testid="onboard-photos-section"><div className="flex items-end justify-between gap-4"><div><p className="text-sm text-ink" data-testid="onboard-photos-label">Studio photos <span className="text-ink/45">({photoUploads.length}/5)</span></p><p className="mt-1 text-xs text-ink/55">Optional JPG, PNG or WEBP images, up to 10MB each.</p></div>{photoUploads.length < 5 && <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full border border-sand px-4 py-2 text-xs text-ink/70 hover:border-espresso ${uploading ? "pointer-events-none opacity-50" : ""}`} data-testid="onboard-photos-upload"><ImagePlus size={14} />{uploading ? "Uploading…" : "Add photos"}<input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={uploading} onChange={(event) => addPhotos(event.target.files)} className="hidden" data-testid="onboard-photos-input" /></label>}</div>{photoUploads.length > 0 && <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">{photoUploads.map((photo, index) => <div key={photo.id} className="relative aspect-square overflow-hidden rounded-md border border-sand bg-sand/30" data-testid={`onboard-photo-${index}`}><span className="flex h-full items-center justify-center px-2 text-center text-xs text-ink/60">{photo.original_filename}</span><button type="button" onClick={() => setPhotoUploads((current) => current.filter((item) => item.id !== photo.id))} className="absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-ink/80 text-bone" data-testid={`onboard-photo-remove-${index}`}><X size={13} /></button></div>)}</div>}</div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="space-y-8" data-testid="onboard-step-3">
            <div>
              <h1 className="font-serif text-4xl md:text-5xl leading-tight">Post your first opening.</h1>
              <p className="mt-2 text-sm text-ink/60">Optional — you can do this later from the dashboard.</p>
            </div>

            <label className="block">
              <span className="block text-xs text-ink/60 mb-1.5">Service</span>
              <input
                type="text"
                value={form.first_service}
                onChange={(e) => set("first_service", e.target.value)}
                className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                placeholder="Balayage refresh"
                data-testid="onboard-first-service"
              />
            </label>

            <div className="grid md:grid-cols-3 gap-6">
              <label className="block">
                <span className="block text-xs text-ink/60 mb-1.5">Usual price</span>
                <input
                  type="number"
                  value={form.first_original_price}
                  onChange={(e) => set("first_original_price", e.target.value)}
                  className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                  placeholder="420"
                  data-testid="onboard-first-price"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-ink/60 mb-1.5">FYLLR offer</span>
                <input
                  type="number"
                  value={form.first_offer_price}
                  onChange={(e) => set("first_offer_price", e.target.value)}
                  className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                  placeholder="295"
                  data-testid="onboard-first-offer"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-ink/60 mb-1.5">Duration (min)</span>
                <input
                  type="number"
                  value={form.first_duration_min}
                  onChange={(e) => set("first_duration_min", e.target.value)}
                  className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                  placeholder="120"
                  data-testid="onboard-first-duration"
                />
              </label>
            </div>

            <label className="block">
              <span className="block text-xs text-ink/60 mb-1.5">When</span>
              <input
                type="text"
                value={form.first_time}
                onChange={(e) => set("first_time", e.target.value)}
                className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                placeholder="Friday · 11:00am"
                data-testid="onboard-first-time"
              />
            </label>
          </div>
        )}

        {/* NAV BUTTONS */}
        {step <= 3 && (
          <div className="mt-12 flex items-center justify-between border-t border-sand pt-6">
            <button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className={`text-sm inline-flex items-center gap-2 ${step === 1 ? "text-ink/30" : "text-ink/60 hover:text-ink"}`}
              data-testid="onboard-prev"
            >
              <ArrowLeft size={14} strokeWidth={1.5} /> Back
            </button>

            {step < 3 ? (
              <button
                onClick={() => canNext() && setStep((s) => s + 1)}
                disabled={!canNext()}
                className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm transition-colors ${
                  canNext() ? "bg-espresso text-bone hover:bg-ink" : "bg-sand text-ink/50 cursor-not-allowed"
                }`}
                data-testid="onboard-next"
              >
                Continue <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={loading}
                className={`inline-flex items-center gap-2 rounded-full bg-espresso text-bone px-6 py-3 text-sm ${loading ? "opacity-60" : "hover:bg-ink"} transition-colors`}
                data-testid="onboard-submit"
              >
                {loading ? "Saving…" : "Continue to membership"} <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
